import { useState, type FormEvent } from 'react'
import type { OrderDetailResponse } from '@fitting-room/contracts'
import { ORDER_MEASUREMENT_KEYS } from '@fitting-room/contracts/order-math'
import { Pencil } from 'lucide-react'
import { useUpdateOrder } from '../../../lib/adminQueries'
import { BOUTIQUE_LOCALE } from '../../../lib/boutique'
import { MEASUREMENT_LABEL, orderNotesChanges, orderNotesDraft, type OrderNotesDraft } from '../../../lib/orders'
import { useStaffToken } from '../../../lib/staffToken'
import { uiStrings } from '../../../lib/strings'
import { FormField, FormSheet } from '../FormSheet'

/** Which Edit opened the panel: the Measurements card's, or the Note card's. */
export type OrderNotesSection = 'measurements' | 'note'

/** The control each Edit puts the cursor in; the panel scrolls to the part around it. */
export const ORDER_NOTES_FOCUS: Record<OrderNotesSection, string> = {
  measurements: 'order-notes-bust',
  note: 'order-notes-note',
}

/** The Edit on the Measurements and Note cards — only while the order is open. */
export function OrderNotesEditButton({
  detail,
  section,
  onEdit,
}: {
  readonly detail: OrderDetailResponse
  readonly section: OrderNotesSection
  readonly onEdit: (section: OrderNotesSection) => void
}) {
  if (detail.order.status !== 'open') return null
  const t = uiStrings(BOUTIQUE_LOCALE)
  return (
    <button
      type="button"
      className="st-btn-quiet print-hide"
      aria-label={section === 'measurements' ? t.orderNotesEditMeasurements : t.orderNotesEditNote}
      onClick={() => onEdit(section)}
    >
      <Pencil size={13} aria-hidden />
      {t.orderNotesEdit}
    </button>
  )
}

/**
 * The customer's measurements, the other request and the note, edited
 * together from the side. CMP-9 § 4: both Edits open this one panel, at the
 * part they belong to. Save sends what changed and no reason — the API takes
 * these three without one, and the history still says who and what.
 *
 * The form is mounted only while the panel is open, so Cancel, the close
 * button and Escape all throw the typing away.
 */
export function OrderNotesPanel({
  detail,
  section,
  onClose,
}: {
  readonly detail: OrderDetailResponse
  readonly section: OrderNotesSection | null
  readonly onClose: () => void
}) {
  const t = uiStrings(BOUTIQUE_LOCALE)
  const focusSection = (event: Event) => {
    if (!section) return
    event.preventDefault()
    const field = document.getElementById(ORDER_NOTES_FOCUS[section])
    field?.closest('[data-order-notes-part]')?.scrollIntoView({ block: 'start' })
    field?.focus({ preventScroll: true })
  }
  return (
    <FormSheet
      open={section !== null}
      onOpenChange={(open) => (open ? undefined : onClose())}
      title={t.orderNotesTitle}
      description={t.orderNotesDescription}
      onOpenAutoFocus={focusSection}
    >
      {section ? <OrderNotesForm detail={detail} onClose={onClose} /> : null}
    </FormSheet>
  )
}

/** The three parts, as inputs. Exported for its render test. */
export function OrderNotesForm({ detail, onClose }: { readonly detail: OrderDetailResponse; readonly onClose: () => void }) {
  const t = uiStrings(BOUTIQUE_LOCALE)
  const { token } = useStaffToken()
  const { order } = detail
  const save = useUpdateOrder(token, order.id)
  const [draft, setDraft] = useState<OrderNotesDraft>(() => orderNotesDraft(order))

  const submit = (event: FormEvent) => {
    event.preventDefault()
    const body = orderNotesChanges(order, draft)
    if (!body) return onClose()
    save.mutate(body, { onSuccess: onClose })
  }

  return (
    <form onSubmit={submit} noValidate aria-label={t.orderNotesTitle}>
      <fieldset className="mb-4" data-order-notes-part="measurements">
        <legend className="st-eyebrow mb-1.5">{t.orderNotesMeasurements}</legend>
        <div className="grid grid-cols-3 gap-2 sm:grid-cols-5">
          {ORDER_MEASUREMENT_KEYS.map((key) => (
            <label key={key} className="flex flex-col gap-1 text-xs">
              <span className="st-meta">{MEASUREMENT_LABEL[key]}</span>
              <input
                id={`order-notes-${key}`}
                className="st-input tabular-nums"
                maxLength={40}
                value={draft.measurements[key]}
                onChange={(event) => {
                  const value = event.target.value
                  setDraft((prev) => ({ ...prev, measurements: { ...prev.measurements, [key]: value } }))
                }}
              />
            </label>
          ))}
        </div>
      </fieldset>

      <div data-order-notes-part="other-request">
        <FormField label={t.orderNotesOtherRequest}>
          <textarea
            id="order-notes-other-request"
            className="st-input"
            rows={3}
            maxLength={4000}
            value={draft.otherRequest}
            onChange={(event) => {
              const value = event.target.value
              setDraft((prev) => ({ ...prev, otherRequest: value }))
            }}
          />
        </FormField>
      </div>

      <div data-order-notes-part="note">
        <FormField label={t.orderNotesNote}>
          <textarea
            id="order-notes-note"
            className="st-input"
            rows={4}
            maxLength={4000}
            value={draft.note}
            onChange={(event) => {
              const value = event.target.value
              setDraft((prev) => ({ ...prev, note: value }))
            }}
          />
        </FormField>
      </div>

      {save.isError ? (
        <p className="st-alert mb-3" role="alert">
          {save.error.message}
        </p>
      ) : null}
      <div className="flex justify-end gap-2">
        <button type="button" className="st-btn-ghost" onClick={onClose}>
          {t.orderNotesCancel}
        </button>
        <button type="submit" className="st-btn" disabled={save.isPending}>
          {save.isPending ? t.orderNotesSaving : t.orderNotesSave}
        </button>
      </div>
    </form>
  )
}
